import { Controller } from "@hotwired/stimulus";
import { resetOnBfcacheRestore } from "../app/helpers/bfcache";
import { translate } from "../app/helpers/classes/translation";

// Replaces rails-ujs remote:true + the ajax:success/ajax:error jQuery
// handlers in load_actions_step2.ts. Preserves the exact two-phase
// contract Step2Controller#make_reservation expects:
//   1. First submit: only_save=true, only_confirm=false. A 200 response
//      with status "ok" and a `passengers` array means the reservation was
//      saved but not finalized -- show the "confirm names match passport"
//      step before continuing (the old #step_2_check_names fancybox).
//   2. Confirming resubmits the SAME form with only_save=false,
//      only_confirm=true, which returns `url` (step3) to navigate to.
// Field names (you[...], trav[...], baggage[uuid][id], etc.) are untouched
// -- this only replaces how the response is handled after submit.
export default class extends Controller {
  static targets = ["errorBanner", "onlySave", "onlyConfirm", "confirmModal", "confirmList", "confirmHeader", "confirmBirthdayHeader", "confirmSpinner", "conditions", "submitButton", "submitSpinner"];

  connect() {
    this.pendingPassengers = null;
    // Back from step3 otherwise restores a disabled Fortsatt with its
    // spinner still up -- see app/helpers/bfcache.js. resetSpinners() is
    // the same undo the error paths already use.
    this.teardownBfcache = resetOnBfcacheRestore(() => this.resetSpinners());
  }

  disconnect() {
    if (this.teardownBfcache) this.teardownBfcache();
  }

  // Old design's exact contract (load_actions_step2.ts, recovered from git
  // history): when seating is offered (#toggle_seating_link visible),
  // step2 refuses to actually submit until the seatmap has at least been
  // opened once (#seating_viewed checked) -- opening it is what's
  // required, not picking a seat. Clicking the link (step2-seating#open)
  // marks it viewed and, when the fancybox closes, re-clicks this same
  // submit button to continue automatically -- see step2_seating_controller.js.
  seatingNotYetViewed() {
    const link = document.getElementById("toggle_seating_link");
    const viewed = document.getElementById("seating_viewed");
    if (!link || !viewed) return false;
    return getComputedStyle(link).display !== "none" && !viewed.checked;
  }

  async submit(event) {
    event.preventDefault();
    this.clearErrors();
    if (this.seatingNotYetViewed()) {
      document.getElementById("toggle_seating_link")?.click();
      return;
    }
    if (!this.validate()) return;
    this.onlySaveTarget.value = "true";
    this.onlyConfirmTarget.value = "false";
    // Same "please wait" spinner every other step-to-step submit in the
    // funnel uses (common/_button_spinner.html.erb) -- this first submit
    // (save, not yet confirmed) previously showed no feedback at all while
    // its request was in flight.
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.remove("hidden");
    await this.postForm();
  }

  // Client-side pass over the same required/pattern fields the backend
  // would otherwise be the only thing to reject -- this form uses
  // novalidate (see _booking_form.html.erb) specifically so this
  // controller, not the browser's own unstyled validation bubble, owns
  // marking which field(s) are wrong. errors is the same [id, [messages]]
  // shape Step2Controller#make_reservation_error already returns (see
  // showFieldErrors below), so both paths share one rendering path.
  validate() {
    const errors = [];

    this.element.querySelectorAll("[required]").forEach((el) => {
      if (el.type === "checkbox" || el.type === "radio" || el.disabled) return;
      if (!el.value || !el.value.trim()) {
        errors.push([el.id, [translate("Det här fältet måste fyllas i")]]);
      }
    });

    const mail = this.element.querySelector('[name="you[mail]"]');
    const mailConfirm = this.element.querySelector('[name="you[mail_confirm]"]');
    if (mail && mailConfirm && mail.value.trim() && mailConfirm.value.trim() && mail.value.trim() !== mailConfirm.value.trim()) {
      errors.push([mailConfirm.id, [translate("E-postadresserna stämmer inte överens")]]);
    }

    this.element.querySelectorAll("input[pattern]").forEach((el) => {
      if (el.value.trim() && !new RegExp(`^(?:${el.pattern})$`).test(el.value.trim())) {
        errors.push([el.id, [translate("Ange datumet som ÅÅÅÅ-MM-DD")]]);
      }
    });

    if (this.hasConditionsTarget && !this.conditionsTarget.checked) {
      errors.push([this.conditionsTarget.id, [translate("Du måste godkänna villkoren för att fortsätta")]]);
    }

    if (errors.length > 0) {
      this.showFieldErrors(errors);
      return false;
    }
    return true;
  }

  async confirmNames(event) {
    event.preventDefault();
    if (this.hasConfirmSpinnerTarget) this.confirmSpinnerTarget.classList.remove("hidden");
    this.onlySaveTarget.value = "false";
    this.onlyConfirmTarget.value = "true";
    await this.postForm();
  }

  closeConfirm() {
    this.confirmModalTarget.classList.add("hidden");
  }

  // Wired on the confirmModal backdrop itself -- event.currentTarget is
  // that backdrop, event.target is whatever was actually clicked, equal
  // only when the click didn't bubble up from the card content inside it.
  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closeConfirm();
  }

  async postForm() {
    let response;
    try {
      // Rails' csrf_meta_tag (rendered in every desktop layout) is what
      // rails-ujs itself reads to attach X-CSRF-Token to every AJAX
      // request it sends -- this plain fetch() replaced rails-ujs'
      // remote:true handling (see the class comment above) but never
      // added that header, relying solely on the form's own hidden
      // authenticity_token input inside the FormData body. This
      // controller's own form (protect_from_forgery with: :null_session
      // in ApplicationController) doesn't hard-reject a failed check with
      // a visible error -- it silently nulls the request's session
      // instead, so a token mismatch here doesn't look like a CSRF error
      // at all, just an unexplained failure to save/confirm on "Fortsätt".
      // Sending the header explicitly matches what every other AJAX call
      // on this site already does and removes that whole class of bug.
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      response = await fetch(this.element.action, {
        method: "POST",
        body: new FormData(this.element),
        headers: { Accept: "application/json", ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}) },
        credentials: "same-origin"
      });
    } catch (e) {
      this.showError();
      return;
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      this.showError();
      return;
    }

    if (data.status === "ok" && data.url && this.onlyConfirmTarget.value === "true") {
      window.location.href = data.url;
      return;
    }

    if (data.status === "ok" && Array.isArray(data.passengers)) {
      this.showConfirmNames(data.passengers);
      return;
    }

    // make_reservation_error returns errors as [[fieldId, [messages]], ...]
    // (see Step2Controller#make_reservation_error) -- same [id, messages]
    // shape validate() builds above, so a server-side rejection (an age
    // restriction, a duplicate booking, anything this form's own
    // pre-submit checks can't know about) gets the same per-field marking
    // instead of just the one generic banner every non-"ok" response fell
    // back to before.
    if (data.status === "error" && Array.isArray(data.errors) && data.errors.length > 0) {
      this.showFieldErrors(data.errors);
      return;
    }

    this.showError();
  }

  showConfirmNames(passengers) {
    if (!this.hasConfirmModalTarget) return;

    // Only show the birthday column when a birthday actually came back.
    // Whether one is collected depends on the gate's secure_flight setting,
    // and on gates that do not collect it this dialog was asking the
    // customer to check a date against their passport that the form never
    // asked them for, under a header with nothing beneath it.
    const showBirthday = passengers.some((pax) => String(pax.birthday || "").trim() !== "");
    const columns = showBirthday ? "grid-cols-3" : "grid-cols-2";

    if (this.hasConfirmBirthdayHeaderTarget) {
      this.confirmBirthdayHeaderTarget.hidden = !showBirthday;
    }
    if (this.hasConfirmHeaderTarget) {
      this.confirmHeaderTarget.classList.toggle("grid-cols-3", showBirthday);
      this.confirmHeaderTarget.classList.toggle("grid-cols-2", !showBirthday);
    }

    this.confirmListTarget.innerHTML = passengers
      .map(
        (pax) => `
        <div class="grid ${columns} gap-3 border-b border-gray-100 py-2 text-sm">
          <div>${pax.firstname || ""}</div>
          <div>${pax.lastname || ""}</div>
          ${showBirthday ? `<div class="text-gray-500">${pax.birthday || ""}</div>` : ""}
        </div>`
      )
      .join("");

    this.confirmModalTarget.classList.remove("hidden");
    this.resetSpinners();
  }

  showError() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = this.errorBannerTarget.dataset.genericError;
    this.errorBannerTarget.classList.remove("hidden");
    this.resetSpinners();
  }

  // Shared by both submits' terminal states (success moving on to the next
  // modal/step, or an error the user needs to fix and retry) -- whichever
  // spinner was showing (this first submit's, or the confirm-names one)
  // always needs to come back off and its button re-enabled.
  resetSpinners() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false;
    if (this.hasSubmitSpinnerTarget) this.submitSpinnerTarget.classList.add("hidden");
    if (this.hasConfirmSpinnerTarget) this.confirmSpinnerTarget.classList.add("hidden");
  }

  // F-ENGINE's error keys (Step2Controller#make_reservation just relays
  // whatever it returns, unchanged by this rebuild -- confirmed live) are
  // a fixed contract this side can't change: you_firstname/you_lastname
  // and trav_0_firstname/trav_0_lastname, referring to the old design's
  // own separate "contact person" name fields. Figma's rebuilt
  // "Kontaktuppgifter" section dropped those in favor of mirroring
  // Resenär 1's own fields instead (step2_contact_sync_controller.js) --
  // real, visible inputs, just under different ids -- so an error keyed
  // to the old ids has nothing to highlight without this remap. Confirmed
  // live: submitting a name F-ENGINE rejects returns
  // [["you_firstname",[...]], ["trav_0_firstname",[...]], ...] and the
  // banner listed every message correctly, but zero fields got the red
  // border, since document.getElementById found nothing for any of them.
  static FIELD_ID_ALIASES = {
    you_firstname: "trav_1_firstname",
    you_lastname: "trav_1_lastname",
    trav_0_firstname: "trav_1_firstname",
    trav_0_lastname: "trav_1_lastname",
    trav_0_titel: "trav_1_titel",
    trav_0_birthday: "trav_1_birthday",
  };

  // errors: [[fieldId, [message, ...]], ...] -- marks each named field
  // (red border for a text/select input, red label text for a checkbox)
  // and lists every message in the banner, then scrolls the first bad
  // field into view. Shared by validate() (client-side) and postForm()'s
  // "error" response (server-side) so both look identical to the user.
  showFieldErrors(errors) {
    this.clearFieldErrors();
    const messages = [];
    let firstEl = null;

    errors.forEach(([id, msgs]) => {
      const resolvedId = id && (this.constructor.FIELD_ID_ALIASES[id] || id);
      const el = resolvedId && document.getElementById(resolvedId);
      if (el) {
        this.markFieldError(el);
        if (!firstEl) firstEl = el;
      }
      if (Array.isArray(msgs)) messages.push(...msgs);
      else if (msgs) messages.push(msgs);
    });

    // One line per DISTINCT message, not per field. Every empty required
    // field pushes the same "Det här fältet måste fyllas i", so a form
    // submitted blank listed that one sentence once per field -- a dozen
    // identical bullets saying the same thing a dozen times, which also
    // buried any genuinely different message (a mismatched e-mail, an
    // unchecked conditions box) somewhere in the middle of them.
    //
    // Nothing is lost by collapsing them: WHICH fields are wrong is
    // carried by markFieldError's red borders plus the scroll to the first
    // one, so the banner only has to say what KINDS of thing are wrong.
    // Set keeps first-seen order, so a mixed set still reads in the order
    // the offending fields appear in the form.
    //
    // Deliberately applied to server-side errors too (both paths land
    // here): F-ENGINE keys some errors to several ids that this file's own
    // FIELD_ID_ALIASES then collapses onto one input -- you_firstname and
    // trav_0_firstname both resolve to trav_1_firstname -- so those
    // arrived as duplicate sentences about a single visible field.
    const uniqueMessages = [...new Set(messages)];

    if (this.hasErrorBannerTarget) {
      if (uniqueMessages.length > 1) {
        this.errorBannerTarget.innerHTML = `<ul class="list-disc space-y-0.5 pl-4">${uniqueMessages
          .map((m) => `<li>${m}</li>`)
          .join("")}</ul>`;
      } else {
        this.errorBannerTarget.textContent = uniqueMessages[0] || this.errorBannerTarget.dataset.genericError;
      }
      this.errorBannerTarget.classList.remove("hidden");
    }

    if (firstEl) firstEl.scrollIntoView({ behavior: "smooth", block: "center" });
    this.resetSpinners();
  }

  markFieldError(el) {
    // Some fields (currently: birthday) render hidden+not-required when
    // this form's own show_date_select-style heuristic decided they
    // weren't needed -- F-ENGINE runs its own, separately-implemented
    // secure_flight? check and can occasionally still require one. Reveal
    // the field AND its explanation note here rather than leaving the user
    // at a dead end (an error naming a field with nothing to type into,
    // and no visible reason why, was previously unrecoverable and looked
    // like a bug). One-way -- clearFieldErrors only strips styling, never
    // re-hides this, so a field revealed once stays revealed/required for
    // the rest of this page's attempts.
    const optionalWrapper = el.closest("[data-step2-optional-field]");
    if (optionalWrapper) {
      optionalWrapper.classList.remove("hidden");
      el.required = true;
      const note = optionalWrapper.querySelector("[data-step2-optional-field-note]");
      if (note) note.classList.remove("hidden");
    }

    if (el.type === "checkbox" || el.type === "radio") {
      const label = el.closest("label");
      if (label) label.classList.add("text-danger", "step2-field-error");
      return;
    }
    el.classList.remove("border-gray-400");
    el.classList.add("border-danger", "step2-field-error");
  }

  clearFieldErrors() {
    this.element.querySelectorAll(".step2-field-error").forEach((el) => {
      el.classList.remove("step2-field-error", "text-danger");
      if (el.classList.contains("border-danger")) {
        el.classList.remove("border-danger");
        el.classList.add("border-gray-400");
      }
    });
  }

  clearErrors() {
    this.clearFieldErrors();
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.classList.add("hidden");
  }
}
